import { rm, combineReducers } from 'utils';
import { BaseAction, BaseInitialState, BaseState, baseFormReducer, baseReducer } from '../Utils';
import {
  ImageAction,
  ImageInitialState,
  ImageState,
  imagesReducer
} from '../Components/ImageUpload/ImageHelper';
import { SupportProductModel } from 'models/SupportProductModel';
import { isCustomerContactValid } from '../Components/CustomerContact/utils';

type InternalState = BaseState &
  ImageState & {
    reasonForReturn: string;
    reasonForReturnError?: string;
    itemCondition: string;
    itemConditionError?: string;
    products: SupportProductModel[];
    productsError?: string;
    optionalMsg: string;
  };

export const returnsInitialState: InternalState = {
  ...BaseInitialState,
  ...ImageInitialState,
  reasonForReturn: '',
  itemCondition: '',
  optionalMsg: '',
  products: []
};

type Action =
  | { type: 'reasonForReturn'; value: string }
  | { type: 'reasonForReturnError'; value: string }
  | { type: 'itemCondition'; value: string }
  | { type: 'itemConditionError'; value: string }
  | { type: 'optionalMsg'; value: string }
  | { type: 'productChanged'; value: SupportProductModel }
  | { type: 'productsError'; value: string }
  | BaseAction
  | ImageAction;

export const returnsReducer = (state: InternalState, action: Action): InternalState => {
  switch (action.type) {
    case 'reasonForReturn':
      return { ...state, reasonForReturn: action.value };
    case 'reasonForReturnError':
      return { ...state, reasonForReturnError: action.value };
    case 'itemCondition':
      return { ...state, itemCondition: action.value };
    case 'itemConditionError':
      return { ...state, itemConditionError: action.value };
    case 'optionalMsg':
      return { ...state, optionalMsg: action.value };
    case 'productChanged':
      return { ...state, products: getProducts(action.value, state.products) };
    case 'productsError':
      return { ...state, productsError: action.value };
    default:
      return state;
  }
};

export const reducer = combineReducers<InternalState, Action>(
  baseFormReducer,
  baseReducer,
  imagesReducer,
  returnsReducer
);

const getProducts = (
  product: SupportProductModel,
  selectedProducts: SupportProductModel[]
): SupportProductModel[] => {
  if (selectedProducts.some(sp => sp.productId === product.productId)) {
    return selectedProducts.map(sp => {
      if (sp.productId === product.productId) {
        return { ...sp, quantity: product.quantity };
      }
      return sp;
    });
  }

  selectedProducts.push({
    productId: product.productId,
    productName: product.productName,
    productRecievedDate: product.productRecievedDate,
    quantity: product.quantity,
    unit: product.unit
  });

  return selectedProducts;
};

export const isValid = (
  state: InternalState,
  addressChanging: boolean,
  dispatch: (action: Action) => void
) => {
  let isValid = true;

  if (addressChanging) {
    dispatch({
      type: 'addressError',
      value: rm('customerpage_returns_validation_error_address')
    });
    isValid = false;
  }

  if (state.reasonForReturn === '') {
    dispatch({
      type: 'reasonForReturnError',
      value: rm('customerpage_returns_validation_error_reason')
    });
    isValid = false;
  }

  if (state.itemCondition === '') {
    dispatch({
      type: 'itemConditionError',
      value: rm('customerpage_returns_validation_error_condition')
    });
    isValid = false;
  }

  // At least one image is needed
  if (!state.images.some(img => img.imageUrl !== '')) {
    dispatch({
      type: 'imagesError',
      value: rm('customerpage_returns_validation_error_images')
    });
    isValid = false;
  }

  if (state.products.length === 0) {
    dispatch({
      type: 'productsError',
      value: rm('customerpage_returns_validation_error_products')
    });
    isValid = false;
  }

  isValid = isCustomerContactValid({ state, valid: isValid, dispatch });

  return isValid;
};

export const handleReasonChange = (value: string | number, dispatch: (action: Action) => void) => {
  dispatch({
    type: 'reasonForReturn',
    value: value.toString()
  });
  dispatch({
    type: 'reasonForReturnError',
    value: ''
  });
};

export const handleConditionChange = (
  value: string | number,
  dispatch: (action: Action) => void
) => {
  dispatch({
    type: 'itemCondition',
    value: value.toString()
  });
  dispatch({
    type: 'itemConditionError',
    value: ''
  });
};

export const productChanged = (
  product: SupportProductModel,
  dispatch: (action: Action) => void
) => {
  dispatch({ type: 'productChanged', value: product });
  dispatch({ type: 'productsError', value: '' });
};

export const getItemConditions = (rm: (key: string) => string) => [
  {
    label: rm('CustomerService_Question_Return_Condition_Sealed'),
    value: rm('CustomerService_Question_Return_Condition_Sealed'),
    id: 'condition-001'
  },
  {
    label: rm('CustomerService_Question_Return_Condition_Unused'),
    value: rm('CustomerService_Question_Return_Condition_Unused'),
    id: 'condition-002'
  },
  {
    label: rm('CustomerService_Question_Return_Condition_Used'),
    value: rm('CustomerService_Question_Return_Condition_Used'),
    id: 'condition-003'
  }
];

export const getReturnReasons = (rm: (key: string) => string) => [
  {
    label: rm('customerservice_question_return_reason_regrets'),
    value: rm('customerservice_question_return_reason_regrets'),
    id: 'reason-001'
  },
  {
    label: rm('CustomerService_Question_Return_Reason_missorder'),
    value: rm('CustomerService_Question_Return_Reason_missorder'),
    id: 'reason-002'
  },
  {
    label: rm('CustomerService_Question_Return_Reason_didntfit'),
    value: rm('CustomerService_Question_Return_Reason_didntfit'),
    id: 'reason-003'
  },
  {
    label: rm('CustomerService_Question_Return_Reason_expectations'),
    value: rm('CustomerService_Question_Return_Reason_expectations'),
    id: 'reason-004'
  }
];
